import { useState } from "react"
import { Box, Button, Card, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material"
import { useDroppable } from "@dnd-kit/core"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { ListItemBox } from "lib/@components/ListItemBox"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { AssignmentChip } from "./assignment-chip"
import { formatHours } from "./format-hours"

/**
 * One trade on the right pane. Drop schedules or folders on it, or click it to assign the
 * current selection. Its chips are the folders, schedules and tasks assigned to it. Its
 * totals are for the regime being viewed; its chips are the skill map's, in every regime.
 */
export function TradeBucket({ trade }) {
    const { selectedCount, readOnly, onAssignSelection, onEditTrade, onRemoveTrade } = useBoundContext()
    const [menuAnchor, setMenuAnchor] = useState(null)
    const canAssign = !readOnly && selectedCount > 0
    const { setNodeRef, isOver } = useDroppable({ id: `trade:${trade.tradeId}`, data: { tradeId: trade.tradeId } })
    const highlighted = isOver || canAssign
    const borderColor = highlighted ? "primary.main" : "divider"
    const background = isOver ? "primary.lighter" : "background.paper"
    // The button's space is kept while nothing is ticked, so ticking a schedule doesn't shift
    // every card sideways just as the user goes to drag onto one
    const assignVisibility = canAssign ? "visible" : "hidden"

    return (
        <Card
            ref={setNodeRef}
            variant="outlined"
            data-cy="trade-bucket"
            // App-owned drop state: set while dragged work is over this trade and would land here
            data-drop-target={isOver || undefined}
            sx={{ p: 1.5, borderColor, bgcolor: background, transition: "background-color 120ms" }}
        >
            <ListItemBox spacing={1}>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <TruncatedTypography variant="body2" fontWeight={500}>
                        {trade.tradeName}
                    </TruncatedTypography>
                    <Typography variant="caption" color="text.secondary" noWrap data-cy="trade-bucket-totals">
                        {trade.scheduleCount} {"schedule".pluralize(trade.scheduleCount)},{" "}
                        {formatHours(trade.annualHours)} in this regime
                    </Typography>
                </Stack>
                <If value={!readOnly}>
                    <Tooltip title="Edit trade">
                        <IconButton
                            size="small"
                            onClick={handleEdit}
                            aria-label={`Edit trade ${trade.tradeName}`}
                            data-cy="trade-bucket-edit"
                        >
                            <Iconify icon="solar:pen-bold" width={18} />
                        </IconButton>
                    </Tooltip>
                    {/* Removing a trade also unmaps its skills, so it sits in a menu rather than
                        as a button on every card */}
                    <IconButton
                        size="small"
                        onClick={openMenu}
                        aria-label={`More options for ${trade.tradeName}`}
                        data-cy="trade-bucket-menu"
                    >
                        <Iconify icon="eva:more-vertical-fill" width={18} />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                        <MenuItem onClick={handleRemove} sx={{ color: "error.main" }} data-cy="trade-bucket-delete">
                            Remove trade
                        </MenuItem>
                    </Menu>
                    <Box sx={{ visibility: assignVisibility }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleAssign}
                            tabIndex={canAssign ? 0 : -1}
                            aria-hidden={!canAssign}
                            data-cy="trade-bucket-assign"
                        >
                            Assign here
                        </Button>
                    </Box>
                </If>
            </ListItemBox>
            <If value={trade.chips.length > 0}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                    <Repeat list={trade.chips} keyFn={chipKey} pass="chip" item={<AssignmentChip />} />
                </Box>
            </If>
        </Card>
    )

    function handleAssign() {
        onAssignSelection(trade.tradeId)
    }

    function handleEdit() {
        onEditTrade?.(trade.tradeId)
    }

    function openMenu(event) {
        setMenuAnchor(event.currentTarget)
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    function handleRemove() {
        closeMenu()
        onRemoveTrade(trade)
    }
}

function chipKey(chip) {
    return chip.id
}
